import React, { useState } from 'react';
import classnames from 'classnames';
import { MacroEditor, MacroEditorConfigProvider } from 'new/elements/MacroEditor';
import { getMentionsFromMacroAttrs } from 'js/components/data_sources/add_ds_form/ConfigureAPIStage';
import { extractDatasetFromApiServerFlow } from 'js/lib/MacroReplacementUtils';
import { MacroBuilder } from './MacroBuilder';
import classes from './RequestBodyEditor.module.scss';

/**
 * Request body editor (POST/PUT/PATCH) for the "Request Body" tab.
 *
 * Lets the user pick a body format — JSON / XML / Raw (text editor with macros +
 * the {-popup) or Form (a KEY/VALUE table). JSON & XML get live validation status.
 * USABILITY-ONLY: every mode writes the same `body.template` key via
 * `onAPIConfigChanged`; the mode is a UI affordance and not persisted to config.
 */

interface FieldOption {
  label: string;
  value: string;
}

type BodyMode = 'json' | 'xml' | 'raw' | 'form';

export interface RequestBodyEditorProps {
  stageIndex: number;
  stageConfig: Record<string, any>;
  apiURLMacros: any;
  parentDataSet: any;
  parentDataSetSamples: any;
  editedSamples: any;
  dateFormatOptions?: FieldOption[];
  timeUnitOptions?: FieldOption[];
  onAPIConfigChanged: (name: string, value: any, stageIndex?: number | null, data?: any) => void;
}

const MODES: { id: BodyMode; label: string }[] = [
  { id: 'json', label: 'JSON' },
  { id: 'xml', label: 'XML' },
  { id: 'raw', label: 'Raw' },
  { id: 'form', label: 'Form' },
];

/** In-box hint (gray placeholder, disappears as soon as the box is focused). */
const PLACEHOLDERS: Record<BodyMode, string> = {
  json: 'Form Data in JSON format',
  xml: 'Form Data in XML format',
  raw: 'Form Data in raw text. For key values use key:value, one per line',
  form: '',
};

/** Macros ({...}) aren't literal JSON; swap them for a string so structure validates. */
const stripMacros = (text: string): string => text.replace(/\{[^{}]*\}/g, '"__macro__"');

/** null = no status (empty), true = valid, false = invalid. */
const validateBody = (mode: BodyMode, value: string): boolean | null => {
  const text = (value ?? '').trim();
  if (!text) return null;
  if (mode === 'json') {
    try {
      JSON.parse(stripMacros(text));
      return true;
    } catch {
      return false;
    }
  }
  if (mode === 'xml') {
    try {
      const doc = new DOMParser().parseFromString(text, 'application/xml');
      return doc.getElementsByTagName('parsererror').length === 0;
    } catch {
      return false;
    }
  }
  return null;
};

const sniffMode = (value: string): BodyMode => {
  const text = (value ?? '').trim();
  if (!text) return 'json';
  try {
    JSON.parse(stripMacros(text));
    return 'json';
  } catch {
    /* not json */
  }
  if (text.startsWith('<')) return 'xml';
  return 'raw';
};

interface FormRow {
  key: string;
  value: string;
}

/** Form rows <-> `key: value` one-per-line text (stored in body.template). */
const parseFormRows = (text: string): FormRow[] =>
  (text ?? '')
    .split('\n')
    .filter((line) => line.trim() !== '')
    .map((line) => {
      const idx = line.indexOf(':');
      if (idx === -1) return { key: line.trim(), value: '' };
      return { key: line.slice(0, idx).trim(), value: line.slice(idx + 1).trim() };
    });

const serializeFormRows = (rows: FormRow[]): string =>
  rows
    .filter((r) => r.key.trim() !== '')
    .map((r) => `${r.key.trim()}: ${r.value}`)
    .join('\n');

export const RequestBodyEditor: React.FC<RequestBodyEditorProps> = ({
  stageIndex,
  stageConfig,
  apiURLMacros,
  parentDataSet,
  parentDataSetSamples,
  editedSamples,
  dateFormatOptions = [],
  timeUnitOptions = [],
  onAPIConfigChanged,
}) => {
  const body: string = stageConfig['body.template'] ?? '';
  const [mode, setMode] = useState<BodyMode>(() => sniffMode(body));

  const setBody = (v: string) => onAPIConfigChanged('body.template', (v ?? '').trim(), stageIndex);

  const { macros } = getMentionsFromMacroAttrs({
    stageIndex,
    apiURLMacros,
    parentDataSet,
    parentDataSetSamples,
    editedSamples,
  });

  const renderMacroPopup = ({
    insert,
    close,
  }: {
    insert: (raw: string) => void;
    close: () => void;
  }) => (
    <MacroBuilder
      macros={macros}
      dateFormat={stageConfig['date.format'] || 'yyyy-MM-dd'}
      dateTimeUnit={stageConfig['date.time.unit'] || 'dd'}
      dateFormatOptions={dateFormatOptions}
      timeUnitOptions={timeUnitOptions}
      onChangeSetting={(k, v) => onAPIConfigChanged(k, v, stageIndex)}
      onInsert={insert}
      onCancel={close}
    />
  );

  const status = validateBody(mode, body);

  // Form mode shows a trailing empty row for adding a new pair.
  const formRows: FormRow[] = [...parseFormRows(body), { key: '', value: '' }];
  const commitForm = (rows: FormRow[]) => setBody(serializeFormRows(rows));
  const updateFormRow = (index: number, patch: Partial<FormRow>) =>
    commitForm(formRows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const removeFormRow = (index: number) => commitForm(formRows.filter((_, i) => i !== index));

  return (
    <div data-testid={`request-body-editor-${stageIndex}`}>
      <div className={classes.modeBar}>
        <span className={classes.modeLabel}>Body format</span>
        <div className={classes.modeTabs}>
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={classnames(classes.modeTab, { [classes.modeTabActive]: mode === m.id })}
              onClick={() => setMode(m.id)}
              data-testid={`body-mode-${m.id}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {status != null && (
          <span
            className={classnames(
              classes.badge,
              status ? classes.badgeValid : classes.badgeInvalid,
            )}
            data-testid="body-validity"
          >
            {status ? '● ' : '▲ '}
            {status ? 'Valid' : 'Invalid'} {mode.toUpperCase()}
          </span>
        )}
      </div>

      {mode === 'form' ? (
        <div className={classes.formGrid} data-testid="body-form-grid">
          <div className={classes.formHead}>
            <span>Key</span>
            <span>Value</span>
            <span />
          </div>
          {formRows.map((row, index) => {
            const isTrailing = index === formRows.length - 1;
            return (
              <div className={classes.formRow} key={index}>
                <input
                  className={classes.formCell}
                  value={row.key}
                  placeholder="Field name"
                  onChange={(e) => updateFormRow(index, { key: e.target.value })}
                  data-testid={`body-form-key-${index}`}
                />
                <input
                  className={classes.formCell}
                  value={row.value}
                  placeholder="Value"
                  onChange={(e) => updateFormRow(index, { value: e.target.value })}
                  data-testid={`body-form-value-${index}`}
                />
                <button
                  type="button"
                  className={classes.formRemove}
                  disabled={isTrailing}
                  aria-label="Remove field"
                  onClick={() => removeFormRow(index)}
                  data-testid={`body-form-remove-${index}`}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <MacroEditorConfigProvider
          dateFormat={stageConfig['date.format'] || 'yyyy-MM-dd'}
          dateTimeUnit={stageConfig['date.time.unit'] || 'dd'}
          parentDatasetSamples={extractDatasetFromApiServerFlow(
            parentDataSet,
            parentDataSetSamples,
            editedSamples,
          )}
        >
          <MacroEditor
            multiline
            rows={8}
            maxRows={12}
            isJson={mode === 'json'}
            value={body}
            onChange={(v: string) => setBody(v)}
            suggestions={macros.filter((m: any) => !m.empty)}
            renderMacroPopup={renderMacroPopup}
            placeholder={PLACEHOLDERS[mode]}
            clearPlaceholderOnFocus
            sx={{ fontSize: '12px' }}
          />
        </MacroEditorConfigProvider>
      )}
    </div>
  );
};

export default RequestBodyEditor;
